import { useCallback, useEffect, useRef } from "react";

import { useLocation } from "@remix-run/react";
import { toast } from "sonner";

import { UPDATE_AVAILABLE_EVENT, isReloadBlocked } from "~/lib/app-update";

// AIDEV-NOTE: Proactive stale-PWA recovery. Polls /api/version on focus and on
// navigation (throttled) and, when the live deploy's build id no longer matches
// the one baked into this bundle (__BUILD_ID__), surfaces a non-blocking "refresh
// to update" toast. This stops users from ever hitting a 404'd dynamic-import
// chunk after a deploy — the reactive vite:preloadError handler in entry.client
// is the safety net for the cases this misses. We only ever PROMPT (never auto-
// reload) so an in-progress recording/upload is never interrupted. See
// app-update.ts. PROD-only — dev build ids never match the server fallback.

const VERSION_ENDPOINT = "/api/version";
const MIN_CHECK_INTERVAL_MS = 60_000;
const UPDATE_TOAST_ID = "app-update-available";

// AIDEV-NOTE: Only run the version check when we booted with a real deploy id.
// Off-Vercel builds bake a `dev-<ts>` placeholder while /api/version falls back
// to "dev" — comparing those would false-positive a prompt forever. Commit SHAs
// are hex, so a real build id can never start with "dev".
const HAS_REAL_BUILD_ID = !__BUILD_ID__.startsWith("dev");

async function fetchLiveBuildId(signal: AbortSignal): Promise<string | null> {
    try {
        const res = await fetch(VERSION_ENDPOINT, {
            signal,
            headers: { Accept: "application/json" },
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { buildId?: unknown };
        return typeof data.buildId === "string" ? data.buildId : null;
    } catch {
        // Network hiccup / aborted — nothing to do, we'll check again later.
        return null;
    }
}

export function AppUpdatePrompt() {
    const location = useLocation();
    const lastCheckRef = useRef(0);

    const showUpdateToast = useCallback(() => {
        toast("A new version is available", {
            id: UPDATE_TOAST_ID,
            description: "Refresh to get the latest improvements and fixes.",
            duration: Infinity,
            action: {
                label: "Refresh",
                onClick: () => {
                    // AIDEV-NOTE: Even on explicit user click, never yank an
                    // active recording/upload out from under them.
                    if (isReloadBlocked()) {
                        toast.error(
                            "Finish your current recording before refreshing.",
                        );
                        return;
                    }
                    window.location.reload();
                },
            },
        });
    }, []);

    const checkForUpdate = useCallback(
        async (signal: AbortSignal) => {
            const now = Date.now();
            if (now - lastCheckRef.current < MIN_CHECK_INTERVAL_MS) return;
            lastCheckRef.current = now;

            const liveBuildId = await fetchLiveBuildId(signal);
            if (liveBuildId && liveBuildId !== __BUILD_ID__) {
                showUpdateToast();
            }
        },
        [showUpdateToast],
    );

    // AIDEV-NOTE: Reactive recovery. The vite:preloadError handler dispatches this
    // when it wants to reload but can't (mid-recording). It's a real-chunk-404
    // signal, independent of build-id config, so it stays armed in any PROD build
    // — NOT gated by HAS_REAL_BUILD_ID (which only guards the proactive poll).
    useEffect(() => {
        if (import.meta.env.DEV) return;
        window.addEventListener(UPDATE_AVAILABLE_EVENT, showUpdateToast);
        return () =>
            window.removeEventListener(UPDATE_AVAILABLE_EVENT, showUpdateToast);
    }, [showUpdateToast]);

    // Proactive poll: needs a real deploy id to compare against, so it's gated.
    useEffect(() => {
        if (import.meta.env.DEV || !HAS_REAL_BUILD_ID) return;

        const controller = new AbortController();
        const run = () => void checkForUpdate(controller.signal);

        // Initial check + re-check whenever the user returns to the tab/app.
        run();
        window.addEventListener("focus", run);

        return () => {
            controller.abort();
            window.removeEventListener("focus", run);
        };
    }, [checkForUpdate]);

    // Re-check on client navigation (cheap and throttled).
    useEffect(() => {
        if (import.meta.env.DEV || !HAS_REAL_BUILD_ID) return;
        const controller = new AbortController();
        void checkForUpdate(controller.signal);
        return () => controller.abort();
    }, [location.pathname, checkForUpdate]);

    return null;
}
